import { useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import styled from "styled-components";
import EventFinished from "src/components/EventFinished";
import Hero from "src/components/Hero/Hero";
import PromoInfoImageGrid from "src/components/Promotions/PromoInfoImageGrid";
import TitleSection from "src/components/TitleSection/TitleSection";

const TermsAccordionSection = ({ section }) => {
  const [isOpen, setIsOpen] = useState(false);
  const tableHeaderBgColor = section?.tableHeaderBgColor || "#cd8d9b";
  const tableHeaderTextColor = section?.tableHeaderTextColor || "#ffffff";

  if (!section) {
    return null;
  }

  return (
    <Row className="g-4 mb-0 mt-0 align-items-center drop-down-text">
      <button
        onClick={() => {
          if (section?.list) {
            setIsOpen(!isOpen);
          }
        }}
      >
        {section?.label}
        <span className={isOpen ? "open" : ""}></span>
      </button>

      <Col xs={12} className={isOpen ? "ol-active pb-4 m-0" : "m-0"}>
        <ReactMarkdown>{section?.title}</ReactMarkdown>

        <ol className="rules-list">
          {(section?.list || []).map((point, index) => (
            <li key={index} className={isOpen ? "ol-active" : ""}>
              <ReactMarkdown>{point}</ReactMarkdown>

              {index === 3 && section?.drawEntries && (
                <table className="table table-bordered my-3 text-center">
                  <thead>
                    <tr>
                      <th style={{ backgroundColor: tableHeaderBgColor, color: tableHeaderTextColor }}>
                        {section.drawEntries.earningPeriod.title}
                      </th>
                      <th style={{ backgroundColor: tableHeaderBgColor, color: tableHeaderTextColor }}>
                        {section.drawEntries.drawPeriod.title}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{section.drawEntries.earningPeriod.text}</td>
                      <td>{section.drawEntries.drawPeriod.text}</td>
                    </tr>
                  </tbody>
                </table>
              )}
            </li>
          ))}
        </ol>

        <ReactMarkdown>{section?.text}</ReactMarkdown>
      </Col>
    </Row>
  );
};

const SpinAndSpoilMom2026 = () => {
  const { t, i18n } = useTranslation();
  const hero = t("spinAndSpoilMom2026.hero", {
    returnObjects: true,
  });
  const heroFrostedPanel = hero?.frostedGlassPanel
    ? {
        ...hero.frostedGlassPanel,
      }
    : null;
  const sections = t("spinAndSpoilMom2026.content.sections", {
    returnObjects: true,
  });

  const renderSection = (section, index) => {
    if (!section?.type) {
      return null;
    }

    if (section.type === "TitleSection") {
      return (
        <TitleSection
          key={`section-${index}`}
          title={section?.props?.title}
          text={section?.props?.text}
          borderColor={section?.props?.borderColor || "#c23c4f"}
          dark={Boolean(section?.props?.dark)}
        />
      );
    }

    if (section.type === "PromoInfoImageGrid") {
      const gridProps = {
        ...section?.props,
        cta: section?.props?.cta
          ? {
              ...section.props.cta,
              url: section.props.cta.url || t("routes.rewards"),
            }
          : null,
      };

      return <PromoInfoImageGrid key={`section-${index}`} section={gridProps} />;
    }

    if (section.type === "TermsAccordion") {
      return <TermsAccordionSection key={`section-${index}`} section={section?.props} />;
    }

    return null;
  };

  return (
    <PageWrap>
      <Hero
        bgImage="promotions/gaming-machines/spin-and-spoil-mom-2026/mothers-day-hero-image-2400x800px.jpg"
        buttonSettings={null}
        image={`promotions/gaming-machines/spin-and-spoil-mom-2026/mothers-day-logo-2400x2400px-${i18n.language}.png`}
        descriptionBgColor="#c7788d"
        frostedGlassPanel={heroFrostedPanel}
      />

      <EventFinished scheduleKeyName="spinAndSpoilMom2026" eventType="promotion" />

      <MainContent>
        <container-div>
          {(Array.isArray(sections) ? sections : []).map(renderSection)}
        </container-div>
      </MainContent>
    </PageWrap>
  );
};

const PageWrap = styled.div`
  width: 100%;
  height: auto;

  .hero_text_container {
    display: none;
  }

  .hero_image_container {
    padding: 20px;
  }

  p strong {
    color: #c23c4f;
  }

  p a {
    color: var(--bluelinklight);
    &:hover {
      text-decoration: underline;
    }
  }
`;

const MainContent = styled.div`
  background-color: #efdce2;
  background-position: top;
  background-repeat: no-repeat;
  background-size: cover;
  height: auto;
  width: 100%;
  text-align: left;

  container-div {
    background: transparent;
  }

  @media (min-width: 992px) {
    padding: 2.8rem 0;
  }

  .row {
    display: flex;
    justify-content: space-between;
    max-width: 1200px;
    margin: 0 auto;
    position: relative;
  }

  .drop-down-text {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;

    button {
      margin: 20px 0;
      width: fit-content;
      border: none;
      height: 40px;
      background-color: #cd8d9b;
      color: var(--white);
      border-radius: 4px;
      padding: 10px 20px;
      display: flex;
      justify-content: flex-start;
      align-items: center;
      cursor: pointer;

      > span {
        display: inline-block;
        width: 0;
        height: 0;
        margin-left: 10px;
        vertical-align: middle;
        border-top: 4px solid;
        border-right: 4px solid transparent;
        border-left: 4px solid transparent;
        transition: transform 0.3s ease-in-out;
      }

      > span.open {
        transform: rotate(180deg);
      }
    }

    div {
      max-height: 0;
      overflow: hidden;
      transition: max-height 0.3s ease-in-out;

      ol {
        padding: 20px;
        margin: 0;

        li {
          padding: 3px;
          font-size: 14px;
          color: #555;
          white-space: normal;

          p {
            margin: 0;
          }
        }
      }

      &.ol-active {
        max-height: fit-content;
      }
    }
  }
`;

export default SpinAndSpoilMom2026;
